import { inject, injectable } from 'inversify';
import Debug from 'debug';
import { scaleScore } from '@/app/shared/utils/scale-score'
import { CommandHandler, createCommand } from '@/app/tracking/api';
import { Scorm2004ObjectiveDataStore, Scorm2004ObjectiveDetails } from '@/app/tracking/scorm/2004/store';
import { CompleteExamTrackingCommand, CompleteExamTrackingCommandPayload, ExamMeta } from '../api'
import { ExamSuspendDataStore, calculateEffecticeGrade } from '../common';
import { ExamAttempt } from '../../domain';

const debug = Debug('scorm2004-complete-exam-command-handler')

@injectable()
export class Scorm2004CompleteExamCommandHandler implements CommandHandler<CompleteExamTrackingCommand> {

  constructor (
    @inject(Scorm2004ObjectiveDataStore)
    private readonly objectiveDataStore: Scorm2004ObjectiveDataStore,
    @inject(ExamSuspendDataStore)
    private examStore: ExamSuspendDataStore,
  ) {}
  
  public async execute(command: CompleteExamTrackingCommandPayload) {

    debug('execute', command)

    await this.examStore.execute(createCommand<CompleteExamTrackingCommand>('complete-exam', command))

    const effectiveGrade = await calculateEffecticeGrade(this.examStore)(command.id, command.effectiveGrading)
    if (effectiveGrade) {
      await this.storeInObjectives(command.id, command.meta, effectiveGrade)
    }
  }

  private async storeInObjectives(id: string, meta: ExamMeta, attempt: ExamAttempt) {
    const mapped = this.mapExamToObjective(id, meta.title, attempt);
    await this.objectiveDataStore.upsert(mapped);
  }

  private mapExamToObjective(id: string, title: string, attempt: ExamAttempt): Scorm2004ObjectiveDetails {

    const score = this.mapScore(attempt)

    return {
      id: id,
      description: title,
      score,
      completionStatus: 'completed',
      successStatus: attempt.result,
      progressMeasure: 1
    };
  }

  private mapScore(attempt: ExamAttempt) {
    const score = attempt.score
    return {
      min: score.min,
      max: score.max,
      raw: score.raw,
      scaled: scaleScore(score, { min: -1, max: 1 })
    }
  }
}
